import { useState, createContext, useEffect } from 'react'
import { toast } from 'react-toastify'

import { assetLocationService } from '@/services/AssetLocation'
import { addressService } from '@/services/utils/addressService'
import { utilityPreparationService } from '@/services/Preparation/utilityPreparationService'
import { utilityService } from '@/services/Parametrization/utilityService'

import { CONSTANTS } from '@/utils/CONSTANTS'
import { normalizeAndReplace } from '@/components/VehicleDataForm/utils'
import { usePermission } from '@/hooks/usePermission'
import { PermissionAction, PermissionResource } from '@/types/permissions.types'

interface IAssetLocation {
  children: React.ReactNode
}

type AssetLocationData = {
  DATA: any
  GET_SELECTS: () => Promise<void>
  CAN_VIEW_INTEGRACOES: boolean
  CAN_EXECUTE_INTEGRACOES: boolean
}

export type IntegrationTypeOptions = {
  id: number
  label: string
}

export const AssetLocationContext = createContext({} as AssetLocationData)

export function AssetLocationProvider({ children }: IAssetLocation) {
  const [DATA, SET_DATA] = useState({})

  const { PERMISSIONS, HAS_PERMISSION } = usePermission()
  const [CAN_VIEW_INTEGRACOES, SET_CAN_VIEW_INTEGRACOES] = useState(false)
  const [CAN_EXECUTE_INTEGRACOES, SET_CAN_EXECUTE_INTEGRACOES] = useState(false)

  const integrationType: IntegrationTypeOptions[] = [
    { id: 1, label: 'Integração Veículo' }
  ]

  const keys = [
    'diretorias',
    'crs',
    'estados',
    'patios',
    'familias',
    'categorias',
    'modelos'
  ]

  useEffect(() => {
    const canViewIntegracoes = HAS_PERMISSION(
      PermissionResource.INTEGRACOES,
      PermissionAction.READ
    )
    const canExecuteIntegracoes = HAS_PERMISSION(
      PermissionResource.INTEGRACOES,
      PermissionAction.EXECUTE
    )

    SET_CAN_EXECUTE_INTEGRACOES(canExecuteIntegracoes)
    SET_CAN_VIEW_INTEGRACOES(canViewIntegracoes)
  }, [PERMISSIONS, HAS_PERMISSION])

  const EXTRACT_VALUES = (arr: any) => {
    const extractedValues: any = {}
    arr.forEach((item: any, index: number) => {
      extractedValues[keys[index]] = item?.data?.data
    })

    extractedValues.tipoIntegracao = integrationType

    SET_DATA(extractedValues)
  }

  const GET_PROVIDER_TYPE = async () => {
    const res: any = await assetLocationService.getProviderType()
    const providerType = res.data.data.rows
    return providerType
  }

  const GET_PROVIDER = async () => {
    try {
      const regexPattern = /patio/i
      const resProviderType = await GET_PROVIDER_TYPE()
      const providerYardId = resProviderType.find((item: any) =>
        normalizeAndReplace(item?.descricao ?? '').match(regexPattern)
      )?.id

      if (!providerYardId)
        throw new Error(
          'Ocorreu um erro ao tentar recuperar os dados dos filtros'
        )

      const resProvider = await assetLocationService.getProvider({
        tiposPrestador: providerYardId,
        pageSize: CONSTANTS.allRegisters,
        ativo: true
      })

      return resProvider
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const GET_SELECTS = async () => {
    try {
      const res = await Promise.all([
        utilityPreparationService.getBoard({ ativo: true }),
        utilityPreparationService.getResultCenter({ ativo: true }),
        addressService.getStates(),
        GET_PROVIDER(),
        utilityService.getModelFamilies({ pageSize: CONSTANTS.allRegisters }),
        utilityService.getModelCategory(),
        utilityService.getModel()
      ])

      EXTRACT_VALUES(res)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <AssetLocationContext.Provider
      value={{
        DATA,
        GET_SELECTS,
        CAN_VIEW_INTEGRACOES,
        CAN_EXECUTE_INTEGRACOES
      }}
    >
      {children}
    </AssetLocationContext.Provider>
  )
}
